/* The callbacks the demo page acts out. YOURS: `copier update` never touches this file.
 *
 * The outbound page puts the visitor on JOHN's side of the desk: somebody rang this
 * morning, here is what they wanted, pick a reply and hand it to the assistant.
 *
 * THIS IS THE REVIEWS, FROM THE OTHER END. Every one-star review on this account is some
 * version of "whenever I rang I was told I would be called back and never was". Every card
 * below is one of those calls, and the button under it is the callback actually happening.
 *
 * THE SHAPE: one contact, three SITUATIONS they might have rung about, and a set of
 * replies per situation. Picking a situation rewrites the note and swaps the replies
 * underneath it, because a reply only means anything against the thing it answers - "Yes,
 * it's ready" is nonsense under "I left a message yesterday". Everything stays editable
 * once picked; these are starting points, not a menu.
 *
 * THE NUMBER MUST BE FAKE. `555` numbers are reserved for fiction and cannot be dialled,
 * and tests/test_client_contract.py holds this file to that. A demo card carrying a real
 * mobile is a real person's number on a page you are about to send to strangers - and the
 * shop's own number is the worst choice of all, because somebody will ring it back.
 */

// Who rang. ONE contact across every situation below - the demo is about the reply, and a
// new name on every chip is a detail to re-read rather than a difference that matters. The
// assistant greets them by this name, so a wrong one ends the call in two seconds rather
// than two minutes, which is exactly what should happen.
export const SAMPLE = {
  number: "(555) 902-1187",
  name: "Sam",
};

/* What they rang about, and what John might say back.
 *
 * `label` is the chip: what they wanted, in three or four words, the way John would think
 * of it. `summary` is the note - the ONLY context the assistant gets, so keep it to facts,
 * and put the reason they rang in the FIRST SENTENCE, because that is what the call gets
 * filed under afterwards.
 *
 * Each `replies` entry is a chip and the answer behind it. Write each `text` as the answer
 * you would actually give down the phone.
 *
 * IT IS NOT READ OUT AS TYPED, and that is deliberate. A reply is an answer to a question
 * the customer asked days ago, and "not yet, sorry" arriving cold is a sentence nobody can
 * follow - so the assistant says what they rang about first and then puts the answer in its
 * own words. What survives that untouched is every fact in it: a day typed here is the day
 * they hear, and so is an hour, a price and a promise.
 *
 * IT IS NOT IN THE OPENING ANY MORE, and that is the one thing that changed here. The
 * opening now names the business, says it is an AI and that the call is recorded, and stops
 * on "am I speaking with Sam?" - because the reply is a customer's job details and whoever
 * picked up the phone might be anybody. The reply is said on the NEXT turn, once they have
 * confirmed. See `autofix/outbound/`.
 *
 * NO PRICE AND NO DIAGNOSIS, and no finish time that the shop will not honour. Those are
 * the three things `autofix/prompts.py` forbids the agent from inventing, and a reply typed
 * here is the one route by which they could still reach a caller - rewording does not
 * scrub a price out, it carries it.
 *
 * EVERY REPLY HAS TO BE AN ANSWER, and every situation has to be one John can answer.
 * "Someone will ring you back" is not a reply - it is a call whose whole content is the
 * promise of another call, which is the exact thing this business is reviewed badly for.
 * Nor is "what did you need?", which is an inbound call wearing a costume. If the note
 * does not hold enough for John to say something real, the NOTE is wrong, not the replies.
 * (A question the note cannot answer is different: the agent says plainly that it is only
 * passing this on and gives them the workshop number, and does not need a chip for it.)
 */
export const SITUATIONS = [
  {
    id: "ready",
    label: "Is my car ready?",
    summary:
      "Rang to ask whether the car is ready. Dropped the Golf in on Monday because it "
      + "wouldn't start; said they can come in any afternoon this week.",
    replies: [
      {
        id: "ready-yes",
        tone: "yes",
        label: "Yes, it's ready",
        text: "Your car's ready. Come and grab it any time we're open.",
      },
      {
        id: "ready-afternoon",
        tone: "later",
        label: "This afternoon",
        text: "It'll be ready this afternoon. Come in any time after three and it'll be "
          + "waiting for you.",
      },
      {
        id: "ready-waiting",
        tone: "hold",
        label: "Need more time",
        text: "Not yet, sorry — the part's on order. We'll ring you the moment it lands.",
      },
      { id: "ready-custom", tone: "custom", label: "Something else", text: "", custom: true },
    ],
  },
  {
    id: "pinkslip",
    label: "Pink slip",
    summary:
      "Rang to ask whether you can do a pink slip this week. Rego is up at the end of the "
      + "month. Did not say what the car is.",
    replies: [
      {
        id: "pinkslip-yes",
        tone: "yes",
        label: "Any morning this week",
        text: "We can do that for you — any morning this week. Drop it in first thing and "
          + "we'll get it done.",
      },
      {
        id: "pinkslip-next",
        tone: "later",
        label: "Next week",
        text: "We're full this week, but next week is open. Any morning you like, and "
          + "you'll still be well inside your rego.",
      },
      { id: "pinkslip-custom", tone: "custom", label: "Something else", text: "", custom: true },
    ],
  },
  {
    // THE COMEBACK, and it is here because it is the call that is worst to miss. Somebody
    // who paid to have a fault fixed and is watching it come back has already decided how
    // this goes; the only question is whether anyone rings them before they decide. It is
    // also a different KIND of answer from the two above - not "when", but "bring it back,
    // and you are not paying twice".
    id: "comeback",
    label: "It's doing it again",
    summary:
      "Rang because the Hilux is doing the same thing again — you had it in a fortnight "
      + "ago for the same fault. Said they can drop it in any day that suits you.",
    replies: [
      {
        id: "comeback-back",
        tone: "yes",
        label: "Bring it straight back",
        text: "Bring it straight back in — any morning this week. We'll take another look "
          + "at it, and there's nothing to pay for that.",
      },
      {
        id: "comeback-monday",
        tone: "later",
        label: "Leave it with us Monday",
        text: "Bring it in Monday morning and leave it with us for the day, so we can see "
          + "it properly rather than rushing it. Nothing to pay for another look.",
      },
      {
        id: "comeback-day",
        tone: "hold",
        label: "We'd need it a full day",
        text: "We'll take another look at it, but we'd want the car for a full day rather "
          + "than an hour — it's the only way to catch something that comes and goes.",
      },
      { id: "comeback-custom", tone: "custom", label: "Something else", text: "", custom: true },
    ],
  },
];

/* The panel under the card. Same job as PITCH on the inbound page and a different
 * argument: that page shows the phone being answered, this one shows the call going back
 * out - which is the half this business keeps being reviewed for. */
export const PITCH = {
  title: "What this is",
  lines: [
    "The call you meant to return. Pick what you'd have told them and send it.",
    "The assistant rings them, says who it is, reminds them what they rang about and "
      + "gives them your answer — your facts, its wording — then checks they've got it "
      + "and rings off. It's a short call, on purpose.",
    "It knows your message and nothing else. Anything it can't answer — what was wrong, "
      + "what it costs, when it'll be done — books you a callback instead of a guess.",
  ],
};
